import { Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { Link } from "react-router-dom";
import HeaderFooter from "./HeaderFooter";
import { BarChart } from "@mui/x-charts/BarChart";

const valueFormatter = (value: number | null) => `${value}%`;

const chartSetting = {
    xAxis: [
      {
        label: 'Percentage (%)',
        max: 100,
      },
    ],
    width: 500,
    margin: {left:75},
    height: 250,

  };

  const chartSetting2 = {
    xAxis: [
      {
        label: 'Percentage (%)',
        max: 100,
      },
    ],
    width: 500,
    margin: {left:75},
    height: 250,

  };
  

const BAR_CHART_DATA = [
    {
        name: "BAYESIAN",
        value: 27,
        notfound: 73 
    },

    {
        name: "SN",
        value: 62,
        notfound: 38
    },

    {
        name: "POISSON",
        value: 69,
        notfound: 31
    },

    {
        name: "OVERALL",
        value: 74,
        notfound: 26
    }

]

const BAR_CHART_DATA2 = [

    {
        name: "SGR",
        value: 90,
        notfound: 10
    },

    {
        name: "GRB",
        value: 72,
        notfound: 28 
    },

    {
        name: "SFL",
        value: 69,
        notfound: 31
    },

    {
        name: "TGF*",
        value: 35,
        notfound: 65
    },
]

const EVENT_DATA = [
    {
        type: "POSSION MODE 1",
        event_count: 133532,
        event_count2: 10087,
        event_count3: 123445,
    },
    {
        type: "POSSION MODE 2",
        event_count: 292416,
        event_count2: 22727,
        event_count3: 269689,
    },
    {
        type: "POSSION MODE 3",
        event_count: 52867,
        event_count2: 4157,
        event_count3: 48710,
    },
    {
        type: "POSSION MODE 4",
        event_count: 301253,
        event_count2: 34022,
        event_count3: 267231,
    },
    {
        type: "SNR MODE 1",
        event_count: 135841,
        event_count2: 7937,
        event_count3: 127904,
    },
    {
        type: "SNR MODE 2",
        event_count: 136431,
        event_count2: 8129,
        event_count3: 128302,
    },
    {
        type: "SNR MODE 3",
        event_count: 67180,
        event_count2: 3267,
        event_count3: 63913,
    },
    {
        type: "SNR MODE 4",
        event_count: 144103,
        event_count2: 15592,
        event_count3: 128551,
    },
    {
        type: "BAYESIAN MODE 1",
        event_count: 94437,
        event_count2: 9811,
        event_count3: 84626,
    },
    {
        type: "BAYESIAN MODE 2",
        event_count: 52448,
        event_count2: 32704,
        event_count3: 19744,
    },
    {
        type: "BAYESIAN MODE 3",
        event_count: 27928,
        event_count2: 3915,
        event_count3: 24013,
    },
    {
        type: "BAYESIAN MODE 4",
        event_count: 33938,
        event_count2: 15903,
        event_count3: 18035,
    },
    {
        type: "TOTAL",
        event_count: 1472374,
        event_count2: 168251,
        event_count3: 1304123,
    },
];

export default function GBMDatabase() {
    const theme = useTheme();

    return (
        <HeaderFooter>
            <Typography style={theme.h1}>GBM Transient Event Database</Typography>

            <Divider style={theme.divider}></Divider>

            <Typography mt={1} fontSize={"15px"}style={{ textAlign: 'justify' }}>
                The event database summary is listed below. We searched for transient
                events using all GBM NaI detector data during the period of 2010 July 16 to 2021 June 30, except 2018 March 18-27 for which no data exists due to the spacecraft operational anomaly. The search types and modes are explained in the 
                <Link to={'/'}> homepage</Link>.
            </Typography>

            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection="column" m={4}>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ textAlign: 'right', fontWeight: "bold" }}>Search Type</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: "bold" }}>Number of Found Events <sup>(a)</sup></TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: "bold" }}>Number of Filtered Events <sup>(b)</sup></TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: "bold" }}>Number of Events in Database</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {EVENT_DATA.map((data, index) => (
                                <TableRow key={`row_${index}`}>
                                    {Object.values(data).map((value, index2) => (
                                        <TableCell key={`cell_${index}_${index2}`}sx={{ textAlign: 'right' }}>{value}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography fontSize={"14px"} style={{ textAlign: 'justify' }}>
                        <p>NOTES –</p>
                        <p><sup>(a)</sup> Same activity can trigger our algorithm multiple times, especially the long ones.</p>
                        <p><sup>(b)</sup> We filtered out the events, which were found right before/after of SAA entrance/exit or at the beginning/end of the TTE data, from our catalog.
                        The details can be found in our search paper. We provide the list of these filtered events as a CSV file. 
                        In the file, we list the method and mode with which the events were found, along with the event times (both UTC and MET), 
                        as well as the triggered detectors and their significance of detections. As for a significance indicator, 
                        we provide Poisson probability for the Poisson method, signal-to-noise ratio for the SN method, and peak burst count rate of the triggered detectors for the Bayesian method. 
                        The list can be downloaded from <a href="https://drive.google.com/drive/folders/1OVOvEryf-KR0h4rEAZci4cumRPEUlHtA?usp=drive_link"> here</a>.</p>
                </Typography>
                <Typography fontSize={"30px"} m={1} fontWeight="bold">
                        Fermi-GBM Triggered Events
                </Typography> 
                
                <img src="trighist_Dec31-2020.png" width="75%" height="auto" />
                
                <Typography fontSize={"14px"} style={{ textAlign: 'justify' }}>
                Trigger history of Fermi-GBM as of June 30, 2021 (i.e., end of our search period). 
                Note that the number of triggers in 2008 and 2021 is less than the years in between due to Fermi’s launch in the half of 2008 and the end of our search in the middle of 2021, respectively.
                OTHERS include UNCERT and Galactic Binary (GALBIN, only three events classified as such).
                </Typography>

                <Typography mt={5} fontSize={"15px"}style={{ textAlign: 'justify' }}>
                <p>During our search period (July 2010 – June 2021), GBM was triggered by 6811 events, of which ~95% have already been classified (event types and/or the sources) on board 
                and reported in <a href="https://heasarc.gsfc.nasa.gov/FTP/fermi/data/tdat/heasarc_fermigtrig.tdat"> Fermi-GBM Trigger Catalog</a>.  
                There are 2588 GRBs, 1144 SFLs, 1056 TGFs, 198 SGRs, 959 LOCLPARs, 79 DISTPARs, 430 TRANSNTs, and 362 UNCERTs.
                We matched these events with the candidate events found in our search.</p>
                </Typography>
                <BarChart
                dataset={BAR_CHART_DATA2}
                yAxis={[{ scaleType: 'band', dataKey: "name" }]}
                series={[
                    { dataKey: 'value', label: 'Found', stack: "total", color: "#1976d2", valueFormatter,},
                    { dataKey: 'notfound', label: 'Not Found', stack: "total", color: "#acc9e6", valueFormatter,},
                ]}
                layout="horizontal"
                grid={{ vertical: true }}
                {...chartSetting2}
                />
                <Typography fontSize={"14px"} style={{ textAlign: 'justify' }}>
                NOTE – Out of 1056, we eliminated 477 TGFs that were detected solely by BGO detectors (GBM algorithm number 119), 
                since this requires a significant increase in count rates of the two BGO detectors independent of NaI detectors 
                but our search includes the NaI detectors only. 
                </Typography>

                <Typography mt={5} fontSize={"15px"}style={{ textAlign: 'justify' }}>
                To match triggered events to our candidate events, the candidate event must coincide both in time and space (direction) with the trigger event.
                For each of the candidate events, we checked whether the trigger time of our candidate event falls within the duration of any of the triggered events and
                compared our “triggered” detectors (i.e., the detectors that satisfy our search criteria) with the detectors that triggered GBM, 
                by comparing the significance (or peak burst count rate in case of the Bayesian method) of the detected event for each “triggered” detector. 
                Based on the results, we provide a trigger grade for each event in our database. 
                In case of match, we also provide the associated trigger number and class given in the Fermi-GBM trigger catalog.
                <p>Grade 0 = Not a Fermi-GBM Trigger (No time match)</p>
                <p>Grade 1 = Only time match (The detectors with the highest significance do not match the triggered detectors.)</p>
                <p>Grade 2 = Time + 1 detector with the highest significance match the triggered detectors.</p>
                <p>Grade 3 = Time + at least 2 detectors with the highest significance match the triggered detectors.</p>
                </Typography>

                <Typography fontSize={"30px"}  m={2} fontWeight="bold">
                        Solar Flares
                </Typography> 
                <Typography mt={1} fontSize={"15px"}style={{ textAlign: 'justify' }}>
                Within our search period, there are 5110 Solar Flares reported in the 
                <a href="https://heasarc.gsfc.nasa.gov/W3Browse/all/fermigsol.html"> Fermi-GBM Solar Flare Catalog</a>.
                Overall, we found 3786 (74%) of them in our search.    
                </Typography>

                <BarChart
                dataset={BAR_CHART_DATA}
                yAxis={[{ scaleType: 'band', dataKey: "name" }]}
                series={[
                    { dataKey: 'value', label: 'Found', stack: "total", color: "#1976d2", valueFormatter,},
                    { dataKey: 'notfound', label: 'Not Found', stack: "total", color: "#acc9e6", valueFormatter,},
                ]}
                layout="horizontal"
                grid={{ vertical: true }}
                {...chartSetting}
                />

                <Typography mt={5} fontSize={"15px"}style={{ textAlign: 'justify' }}>   
                For each event found in our search, we checked whether the event MET falls into any of the SFL intervals in the catalog and
                our "triggered detectors" match with the ones that observe the Sun at the event MET. 
                Based on the results, we provide a solar flare grade for each event in our database:
                <p>Grade 0 = Not SFL        (There is no time match.)</p>
                <p>Grade 1 = Could be SFL   (The detectors with the highest significance do not match the detectors with the smallest angles to the Sun.)</p>
                <p>Grade 2 = Likely SFL     (2 detectors are within the highest 4 significance and smallest 4 angles.)</p>
                <p>Grade 3 = Definitely SFL (At least 3 detectors are within the highest 4 significance and smallest 4 angles.)</p>

                </Typography>

                <Typography fontSize={"30px"} m={2} fontWeight="bold">
                Known X-ray Source Activities
                </Typography> 

                <Typography fontSize={"15px"}style={{ textAlign: 'justify' }}>   
                The outbursts of four bright X-ray transient sources – V404 CYG (in June 2015), Swift J0243.6+6124 (in November 2017), MAXI J1820+070 (in April 2018), and 1A 0535+262 (in November 2020) 
                – triggered Fermi-GBM multiple times, which are also detected in our search.  
                To identify the activities of X-ray sources in our search,
                we checked whether the event MET falls into any of the known activities of the sources listed above and
                our "triggered detectors" match with the ones that observe the source ({"<"} 60°) at the event MET. 
                Based on the results, we provide a transient grade for each event in our database: 

                <p>Grade 0 = Not TRANSNT        (There is no time match or the source is under the Earth blockage.)</p>
                <p>Grade 1 = Could be TRANSNT   (The detectors with the highest significance do not match the detectors with the smallest angles to the source.)</p>
                <p>Grade 2 = Likely TRANSNT     (1 detector within the highest 2-3 significance matches one of the smallest 2-3 angles.)</p>
                <p>Grade 3 = Most Likely TRANSNT (At least 2 detectors are within the highest 2-3 significance and smallest 2-3 angles.)</p>
                
                <p> These four sources triggered the GBM detectors a total of 414 times during their active periods. 
                    Our search identified a total of 420,335 triggered and untriggered signals from these sources (Grade 3 in all modes and methods).</p>
                </Typography>


                <Typography fontSize={"30px"} m={2} fontWeight="bold">
                Particle Events
                </Typography> 

                <Typography fontSize={"15px"}style={{ textAlign: 'justify' }}>   
                <p> We also flagged all particle event candidates in our search using the spacecraft's geographical latitude and longitude at the time of each candidate event. 
                The particle events mostly originate from the charged particles trapped by the Earth’s magnetic field, and hence occur at the edge of the SAA region or higher McIlwain L coordinates ({"≥"} 1.3) as shown in figure belown. 
                Therefore, we flagged the candidate events that occurred when the spacecraft was located at {"≥"} 1.3 or at the SAA entrance region as potential particle events.</p>
                </Typography>

                <img src="particle.png" width="75%" height="auto" />
                <Typography fontSize={"14px"} style={{ textAlign: 'justify' }}>
                Fermi spacecraft geographic coordinates at the times of GBM-triggered local particle events (green dots) and distant particle events (red dots).  
                The SAA region defined by Fermi team is shown in red.  
                The blue shades show the regions with L {"≥"} 1.3 and the SAA entrance region, which we used to flag our candidates as particle events.
                </Typography>

                <Typography fontSize={"15px"} mt = {5}  style={{ textAlign: 'justify' }}>
                <p>Following our definition of particle event flags based on the spacecraft positions, about one third of all event candidates were flagged as potential particle events (flag = 1) in all search Modes.  
                Note that the particle event flag of 1 only indicates an elevated probability of the event being due to charged particles, and does not mean definitive classification.</p>
                </Typography>

                

            </Box>


        </HeaderFooter>
    );
}
